"use client";

import { useQuery } from "@apollo/client/react";
import { DrawerPreview as Drawer } from "@base-ui/react/drawer";
import { getCurrentDate, toPlainDateString } from "@sa-nu/shared-utils/date";

import { graphql } from "../../../__generated__/gql";
import { getDefaultReservationLeadMonths } from "../../../constants";
import { CloseIcon } from "../dx-icons";
import { DETAIL_DRAWER_POPUP_HEIGHT_CLASS } from "../room-detail/detail-drawer-layout";
import { useRoomTypeReserveContext } from "../room-type-reservation/_context/room-type-reserve-context";
import { computePendingStayRange } from "../room-type-reservation/_utils/compute-pending-stay-range";

import { PendingStaySelectionProvider } from "./_context/pending-stay-selection-context";
import { SiteDetailBody } from "./body/site-detail-body";
import type { SiteDetailDrawerPayload } from "./site-detail-drawer-provider";

// DxHotelJsonLd_Site は SEO 用構造化データのため、モーダルには含めない。
// site 情報・部屋タイプの構造フィールドは軽量なので常に取得し、重い availabilities だけ @include で
// 分離する（use-room-type-search.ts と同じ二段フェッチ）。
export const SiteDetailDrawerQuery = graphql(`
  query SiteDetailDrawer(
    $siteSlug: String!
    $provider: AccommodationReservationProviderInput!
    $firstNight: PlainDate!
    $lastNight: PlainDate!
    $withDeferred: Boolean!
    $evChargerAvailable: Boolean
  ) {
    siteBySlug(slug: $siteSlug) {
      id
      ...SiteHero_Site
      ...SiteAboutOverview_Site
      ...SiteAboutFacilities_Site
      ...DxAccessSection_Site
      ...DxFaqSection_Site
      roomTypesForReservation(
        provider: $provider
        evChargerAvailable: $evChargerAvailable
      ) {
        id
        displayName
        architectureType {
          id
          name
          series
          capacity
          content {
            id
            capacityDescription
          }
        }
        features {
          id
          slug
        }
        ...FeatureSubtext_RoomType
        availabilities(
          firstNight: $firstNight
          lastNight: $lastNight
          provider: $provider
          evChargerAvailable: $evChargerAvailable
        ) @include(if: $withDeferred) {
          fromPrice
          nights {
            night
            availableAccommodations {
              id
            }
          }
        }
      }
    }
  }
`);

/** loading / error / not-found 共通の最小フォールバック表示（room-detail の RoomDetailError に揃える）。 */
function SiteDetailMessage({ children }: { children: string }) {
  return (
    <div
      className="bg-dx-surface-base flex min-h-[200px] items-center justify-center px-[16px] py-[40px]"
      role="status"
    >
      <p className="font-notojp text-dx-text-default text-[14px] leading-[1.6]">
        {children}
      </p>
    </div>
  );
}

export function SiteDetailDrawer({
  payload,
  onClose,
}: {
  payload: SiteDetailDrawerPayload | null;
  onClose: () => void;
}) {
  // SiteDetailDrawer は RoomTypeReservationContainer 配下 (RoomTypeReserveProvider の子孫) にのみ
  // 配置される前提。 null は配線ミスなので早期に落とす（`as` を避けるための型ガード）。
  const reserveCtx = useRoomTypeReserveContext();
  if (reserveCtx === null) {
    throw new Error(
      "SiteDetailDrawer must be rendered within a RoomTypeReserveProvider",
    );
  }
  // payload.selectedNight（カレンダーセルタップ）も reserveCtx.firstNight（確定済み日程フィルタ）も
  // 無ければ、真に日程未選択（null）。PendingStaySelectionProvider にもそのまま null を渡し、
  // today への暗黙フォールバックにしない（select-visible-areas.ts の「dateRange が null なら
  // 範囲判定をスキップする」という一覧側の設計と揃える）。
  const trueSelectedNight = payload?.selectedNight ?? reserveCtx.firstNight;
  const isDateUnselected = trueSelectedNight === null;
  // availabilities クエリの range は today 基準の値が要るため、ここだけ today にフォールバックする
  // （fromPrice を拠点一覧と同じ「getDefaultReservationLeadMonths() ヶ月以内の最安値」にするため）。
  const selectedNight = trueSelectedNight ?? getCurrentDate();
  const pendingStay = computePendingStayRange({
    selectedNight,
    existingFirstNight: reserveCtx.firstNight,
    existingLastNight: reserveCtx.lastNight,
  });
  const availabilityRange = isDateUnselected
    ? {
        firstNight: selectedNight,
        lastNight: selectedNight.add({
          months: getDefaultReservationLeadMonths(),
        }),
      }
    : pendingStay;

  const baseVariables = {
    siteSlug: payload?.siteSlug ?? "",
    provider: reserveCtx.provider,
    firstNight: toPlainDateString(availabilityRange.firstNight),
    lastNight: toPlainDateString(availabilityRange.lastNight),
    // 部屋詳細のカレンダー・確認ページと同じ EV 条件で空室・最安値を出す。false は一覧と同じく
    // 「フィルタなし」として null で渡す。
    evChargerAvailable: reserveCtx.evChargerAvailable ? true : null,
  };

  // 構造のみ（軽量・先に返る）。site 情報と部屋タイプ一覧を先に描画し、体感のちらつきを防ぐ。
  const { data: structureData, error: structureError } = useQuery(
    SiteDetailDrawerQuery,
    {
      variables: { ...baseVariables, withDeferred: false },
      skip: payload === null,
      fetchPolicy: "cache-first",
    },
  );

  // availabilities 込み（重い・遅れて返る）。届き次第 Apollo の正規化キャッシュで
  // structureData 側の RoomType.availabilities も自動更新される。
  const { data: fullData, error: fullError } = useQuery(SiteDetailDrawerQuery, {
    variables: { ...baseVariables, withDeferred: true },
    skip: payload === null,
    fetchPolicy: "cache-first",
  });

  // モーダル内の部屋タイプ一覧は価格順ソートをせずグループ化のみのため、一覧ページと違い
  // 日程フィルタ時に在庫が揃うまで待つ例外は不要（在庫到着による並べ替えちらつきが起きない）。
  const data = fullData ?? structureData;
  const loading = !structureData && !fullData;
  const error = fullError ?? structureError;

  const site = data?.siteBySlug ?? null;

  return (
    <Drawer.Root
      open={payload !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Drawer.Portal>
        {/* room-detail の Drawer (z-50) より低い z-40 にし、同時に開いた場合は room-detail を前面にする */}
        <Drawer.Backdrop className="fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0" />
        <Drawer.Viewport className="fixed inset-x-0 bottom-0 z-40 mx-auto max-w-[640px]">
          <Drawer.Popup
            className={`bg-dx-surface-base flex w-full flex-col overflow-hidden rounded-t-[16px] pb-[env(safe-area-inset-bottom)] shadow-lg transition-transform duration-200 data-ending-style:translate-y-full data-starting-style:translate-y-full ${DETAIL_DRAWER_POPUP_HEIGHT_CLASS}`}
          >
            <div className="flex shrink-0 justify-center pt-[12px] pb-[8px]">
              <div className="bg-dx-border-default h-[4px] w-[40px] rounded-full" />
            </div>
            <Drawer.Close
              className="bg-dx-surface-base text-dx-text-default absolute top-[32px] right-[28px] z-10 flex size-[32px] items-center justify-center rounded-full"
              aria-label="閉じる"
            >
              <CloseIcon />
            </Drawer.Close>
            <div className="overflow-y-auto">
              {/* Drawer.Root/Popup は閉状態でも mount され続けるが、payload && ブロックは
                  close で即座に unmount されるため、PendingStaySelectionProvider は open のたびに
                  新規 mount され一時選択状態は前回の開封から引き継がれない。 */}
              {payload && (
                <PendingStaySelectionProvider selectedNight={trueSelectedNight}>
                  {loading ? (
                    <SiteDetailMessage>読み込み中です。</SiteDetailMessage>
                  ) : error || site === null ? (
                    <SiteDetailMessage>
                      拠点情報を取得できませんでした。時間をおいて再度お試しください。
                    </SiteDetailMessage>
                  ) : (
                    <SiteDetailBody
                      site={site}
                      siteSlug={payload.siteSlug}
                      openedFromCalendarCell={
                        payload.selectedNight !== undefined
                      }
                    />
                  )}
                </PendingStaySelectionProvider>
              )}
            </div>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
